import envFromHostname from '@toasttab/env-from-hostname'

let HEAP_ID
switch (envFromHostname(window.location.hostname)) {
  case 'dev':
    HEAP_ID = '2379410469'
    break
  case 'preprod':
    HEAP_ID = '1394490384'
    break
  case 'prod':
    HEAP_ID = '1926653669'
    break
  default:
    HEAP_ID = '1926653669'
    break
}

/* Ignoring function coverage. It loads boilerplate into the browser. */
/* istanbul ignore next */
const heapInit = (loadedCb) => {
  window.heap = window.heap || []
  if (window.heap.load) {
    // heap is loaded
    if (typeof loadedCb === 'function') {
      loadedCb()
    }
    return
  }
  /* istanbul ignore next */
  window.heap.load = (e, t) => {
    window.heap.appid = e
    window.heap.config = t = t || {}
    let r = document.createElement('script')
    r.type = 'text/javascript'
    r.async = !0
    r.src = `https://cdn.heapanalytics.com/js/heap-${e}.js`
    if (typeof loadedCb === 'function') {
      r.onload = loadedCb
    }
    let a = document.getElementsByTagName('script')[0]
    a.parentNode.insertBefore(r, a)
    if (window.heap.clearEventProperties) {
      window.heap.clearEventProperties()
    }
    /* istanbul ignore next */
    const heapLoadTracker = setInterval(() => {
      try {
        if (performance.timing.domComplete !== 0) {
          const loadTime = (performance.timing.domComplete - performance.timing.navigationStart) / 1000
          window.heap.track('Page Load', {
            'Load Time': loadTime
          })
          clearInterval(heapLoadTracker)
        }
      } catch (e) {
        clearInterval(heapLoadTracker)
      }
    }, 200)
  }
  for (
    let n = (e) => {
        return (...argv) => {
          if (window.heap && window.heap.push && typeof window.heap.push === 'function') {
            window.heap.push(
              [e].concat(
                Array.prototype.slice.call(
                  argv,
                  0
                )
              )
            )
          }
        }
      },
      p = [
        'addEventProperties',
        'addUserProperties',
        'clearEventProperties',
        'identify',
        'resetIdentity',
        'removeEventProperty',
        'setEventProperties',
        'track',
        'unsetEventProperty'
      ],
      o = 0;
    o < p.length;
    o++
  ) {
    window.heap[p[o]] = n(p[o])
  }
}

const heapLoad = () => {
  if (window.heap && window.heap.load) {
    window.heap.load(HEAP_ID)
  }
}

export const load = (loadedCb) => {
  heapInit(loadedCb)
  heapLoad()
}

const wait = () => new Promise(resolve => setTimeout(() => resolve(), 0))

/**
 * Track an event in Heap https://docs.heap.io/reference#track
 * @param string eventName Name of the event in Heap - cannot be click, change, or submit
 * @param PlainObject data Plain Object of anonymous structure
 */
export const track = async (eventName, data = {}) => {
  await wait()
  if (
    window.heap &&
    window.heap.track &&
    eventName !== 'click' &&
    eventName !== 'change' &&
    eventName !== 'submit'
  ) {
    window.heap.track(eventName, data)
  }
}

/**
 * Identify a user in Heap https://docs.heap.io/reference#identify
 * @param string identity Unique identifier for a user
 */
export const identify = async (identity) => {
  await wait()
  if (window.heap && window.heap.identify) {
    window.heap.identify(identity)
  }
}

/**
 * Reset an identity to an anonymous state
 * https://docs.heap.io/reference#resetidentity
 */
export const resetIdentity = async () => {
  await wait()
  if (window.heap && window.heap.resetIdentity) {
    window.heap.resetIdentity()
    window.heap.clearEventProperties()
  }
}

/**
 * Add properties to the user's session. All subsequent events will
 * include these properties. The event properties will be cleared in
 * the next browser load. Max length per property is 1024.
 * https://docs.heap.io/reference#addeventproperties
 * @param object eventProps Plain Object with properties to apply
 */
export const addEventProperties = async (eventProps) => {
  await wait()
  if (window.heap && window.heap.addEventProperties) {
    const sanitizedEventProps = Object.entries(eventProps)
      .reduce((acc, [key, val]) => {
        if (typeof val === 'number') {
          acc[key] = val
        } else if (typeof val === 'string' || typeof val === 'boolean') {
          acc[key] = val.toString().substring(0, 1024)
        } else {
          acc[key] = JSON.stringify(val).substring(0, 1024)
        }
        return acc
      }, {})
    window.heap.addEventProperties(sanitizedEventProps)
  }
}

export const removeEventProperty = async (eventPropertyName) => {
  await wait()
  if (window.heap && window.heap.removeEventProperty) {
    window.heap.removeEventProperty(eventPropertyName)
  }
}
